/**
 * Portfolio Thesis Form Component
 *
 * A form component for managing portfolio-level investment thesis.
 * Includes target risk level, target return, target cash flow, comment,
 * and investment style (SOUL) with 5 archetype parameters.
 *
 * Multi-version support: Each user can have multiple thesis versions.
 * Users can view historical versions and create new versions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select, SelectOption } from '../ui/Select';
import { PortfolioThesisChangelog } from './PortfolioThesisChangelog';
import { useTheme } from '../../contexts/ThemeContext';
import { showSuccess, showError, showConfirm } from '../../utils/alert';
import {
  usePortfolioThesis,
  useCreatePortfolioThesis,
  useUpdatePortfolioThesis,
  useDeletePortfolioThesis,
} from '../../services/api/queries/usePortfolioThesis';
import { useUserSettings } from '../../services/api/queries/useUserSettings';
import { useCreatePersonaVersion } from '../../services/api/queries/usePersona';
import type { KnowledgeLevel } from '../../services/api/personaApi';
import type { PortfolioThesis } from '../../services/api/portfolioThesisApi';
import { usePortfolioNavigate } from '../../navigation/PortfolioQualifierContext';
import { useTranslation } from 'react-i18next';
import {
  ARCHETYPES,
  isArchetypeName,
  getArchetypeCode,
  getArchetype,
  REBALANCE_DIRECTIONS,
  EXIT_TRIGGERS,
  TURNOVER_TOLERANCES,
} from '../../config/souls';

export interface PortfolioThesisFormData {
  targetRiskLevel: 'Conservative' | 'Moderate' | 'Balanced' | 'Growth';
  targetReturnLevel: string;
  targetCashFlow: string;
  comment: string;
  /** ε override in PERCENT (the wire value is a fraction, 0.02–0.25). */
  esTargetOverride: string;
}

export interface SoulParamsData {
  styleName: string;
  styleDescription: string;
  targetPositions: string;
  maxSingleWeight: string;
  rebalanceDirection: string;
  exitTrigger: string;
  turnoverTolerance: string;
}

// UI-only preview percentages. The backend's sizing_config resolver remains
// authoritative; this map lets the unsaved form react immediately to a profile
// selection without round-tripping to the API.
const RISK_PROFILE_ES_TARGET_PERCENT: Record<PortfolioThesisFormData['targetRiskLevel'], number> = {
  Conservative: 5,
  Moderate: 8,
  Balanced: 11,
  Growth: 15,
};

const EMPTY_SOUL_PARAMS: SoulParamsData = {
  styleName: '',
  styleDescription: '',
  targetPositions: '',
  maxSingleWeight: '',
  rebalanceDirection: '',
  exitTrigger: '',
  turnoverTolerance: '',
};

/* ------------------------------------------------------------------ */
/*  Knowledge questions (investor persona, T23)                        */
/* ------------------------------------------------------------------ */

const KNOWLEDGE_LEVELS: KnowledgeLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];

// Rank used by the "highest answered" rule in handleSave.
const KNOWLEDGE_RANK: Record<KnowledgeLevel, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
  expert: 3,
};

const KNOWLEDGE_QUESTIONS = ['q1', 'q2', 'q3'] as const;
type KnowledgeQuestionId = (typeof KNOWLEDGE_QUESTIONS)[number];

/**
 * Form-shaped view of one thesis version.
 *
 * One function because four call sites build `formData` (populate, version
 * switch, cancel, empty) and a new field added to only three of them is the
 * classic way an input silently reverts on the next render.
 */
export function formDataFromThesis(
  thesis: PortfolioThesis | null | undefined
): PortfolioThesisFormData {
  return {
    targetRiskLevel:
      (thesis?.target_risk_level as PortfolioThesisFormData['targetRiskLevel']) || 'Moderate',
    targetReturnLevel: thesis?.target_return_level?.toString() || '',
    targetCashFlow: thesis?.target_cash_flow?.toString() || '',
    comment: thesis?.comment || '',
    // The API stores a fraction; the UI shows percent so the copy in the Sizing
    // Coach ("down about 8%") and this field read the same way. Round before
    // stringifying: 0.07 * 100 is 7.000000000000001, which would otherwise show
    // up in the input AND make the form look permanently dirty.
    esTargetOverride:
      thesis?.es_target_override != null
        ? String(Number((Number(thesis.es_target_override) * 100).toFixed(4)))
        : '',
  };
}

// Helper function to get risk level options (translated)
const getRiskLevelOptions = (t: any): SelectOption[] => [
  {
    value: 'Conservative',
    label: t('portfolio.riskConservative', { defaultValue: 'Conservative' }),
    description: t('portfolio.riskConservativeDesc', {
      defaultValue: 'Lower risk, lower return potential',
    }),
  },
  {
    value: 'Moderate',
    label: t('portfolio.riskModerate', { defaultValue: 'Moderate' }),
    description: t('portfolio.riskModerateDesc', { defaultValue: 'Balanced risk/return profile' }),
  },
  {
    value: 'Balanced',
    label: t('portfolio.riskBalanced', { defaultValue: 'Balanced' }),
    description: t('portfolio.riskBalancedDesc', { defaultValue: 'Equal growth and income focus' }),
  },
  {
    value: 'Growth',
    label: t('portfolio.riskGrowth', { defaultValue: 'Growth' }),
    description: t('portfolio.riskGrowthDesc', {
      defaultValue: 'Higher risk, higher return potential',
    }),
  },
];

export function PortfolioThesisForm() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigateToPortfolio = usePortfolioNavigate();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Get translated risk level options
  const RISK_LEVEL_OPTIONS = useMemo(() => getRiskLevelOptions(t), [t]);

  const CUSTOM_SOUL_KEY = '__custom__';
  const [soulPickerOpen, setSoulPickerOpen] = useState(false);

  // Get user's base currency
  const { data: userSettings } = useUserSettings();
  const baseCurrency = userSettings?.base_currency || 'USD';

  // Version state
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);

  // Form state
  const [formData, setFormData] = useState<PortfolioThesisFormData>(formDataFromThesis(null));
  const esTargetProfilePercent = RISK_PROFILE_ES_TARGET_PERCENT[formData.targetRiskLevel];
  const esTargetProfileLabel =
    RISK_LEVEL_OPTIONS.find((option) => option.value === formData.targetRiskLevel)?.label ??
    formData.targetRiskLevel;
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [soulStyle, setSoulStyle] = useState<string | null>(null); // archetype displayName or '__custom__'
  const [customSoulText, setCustomSoulText] = useState('');
  const [soulParams, setSoulParams] = useState<SoulParamsData>({ ...EMPTY_SOUL_PARAMS });
  // Fetch all thesis versions
  const { data: theses = [], isLoading } = usePortfolioThesis();

  // Mutations
  const createMutation = useCreatePortfolioThesis();
  const updateMutation = useUpdatePortfolioThesis();
  const deleteMutation = useDeletePortfolioThesis();
  const createPersonaMutation = useCreatePersonaVersion();

  // Knowledge questions: per-question selected level, undefined = skipped.
  // Deliberately NOT reset by thesis version switches — the persona is
  // user-level, not per-version, and answers should survive a version browse.
  const [knowledgeAnswers, setKnowledgeAnswers] = useState<
    Partial<Record<KnowledgeQuestionId, KnowledgeLevel>>
  >({});

  // Reset selected index if theses array changes and index is out of bounds
  useEffect(() => {
    if (selectedVersionIndex >= theses.length) {
      setSelectedVersionIndex(0);
    }
  }, [theses.length, selectedVersionIndex]);

  // Derive SOUL state from the latest thesis style_name
  useEffect(() => {
    if (theses.length > 0) {
      const latest = theses[0];
      const style = latest.style_name || null;
      if (style && !isArchetypeName(style)) {
        setSoulStyle(CUSTOM_SOUL_KEY);
        setCustomSoulText(style);
      } else if (style) {
        setSoulStyle(style);
      }
      // Populate params — fall back to archetype defaults if thesis columns are empty
      const code = style ? getArchetypeCode(style) : null;
      const arch = code ? getArchetype(code) : null;
      setSoulParams({
        styleName: style || '',
        styleDescription: latest.style_description || (arch ? arch.description : ''),
        targetPositions:
          latest.target_positions || (arch ? arch.defaultParams.target_positions : ''),
        maxSingleWeight:
          latest.max_single_weight || (arch ? arch.defaultParams.max_single_weight : ''),
        rebalanceDirection:
          latest.rebalance_direction || (arch ? arch.defaultParams.rebalance_direction : ''),
        exitTrigger: latest.exit_trigger || (arch ? arch.defaultParams.exit_trigger : ''),
        turnoverTolerance:
          latest.turnover_tolerance || (arch ? arch.defaultParams.turnover_tolerance : ''),
      });
    }
  }, [theses]);

  // Get currently selected thesis
  const currentThesis =
    theses.length > 0 && selectedVersionIndex >= 0 ? theses[selectedVersionIndex] : null;

  // Helper to format version date for selector
  const formatVersionDate = (dateString: string): string => {
    let normalizedDateString = dateString;
    const hasTimezone =
      dateString.includes('Z') ||
      dateString.includes('+') ||
      (dateString.includes('T') && dateString.includes('-', dateString.indexOf('T') + 1));
    if (!hasTimezone && dateString.includes('T')) {
      normalizedDateString = dateString + 'Z';
    } else if (!hasTimezone && dateString.includes(' ')) {
      normalizedDateString = dateString.replace(' ', 'T') + 'Z';
    }
    const date = new Date(normalizedDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatLastUpdated = (dateString: string): string => {
    const date = new Date(dateString);
    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return t('portfolio.lastUpdated', { defaultValue: 'Last updated: {{date}}', date: formatted });
  };

  // Populate form when current thesis changes
  useEffect(() => {
    if (currentThesis) {
      setFormData(formDataFromThesis(currentThesis));
      // Sync SOUL state from style_name
      const style = currentThesis.style_name || null;
      if (style && !isArchetypeName(style)) {
        setSoulStyle(CUSTOM_SOUL_KEY);
        setCustomSoulText(style);
      } else if (style) {
        setSoulStyle(style);
      } else {
        setSoulStyle(null);
        setCustomSoulText('');
      }
      // Build soulParams — fall back to archetype defaults if params are empty
      const code = style ? getArchetypeCode(style) : null;
      const arch = code ? getArchetype(code) : null;
      setSoulParams({
        styleName: style || '',
        styleDescription: currentThesis.style_description || (arch ? arch.description : ''),
        targetPositions:
          currentThesis.target_positions || (arch ? arch.defaultParams.target_positions : ''),
        maxSingleWeight:
          currentThesis.max_single_weight || (arch ? arch.defaultParams.max_single_weight : ''),
        rebalanceDirection:
          currentThesis.rebalance_direction || (arch ? arch.defaultParams.rebalance_direction : ''),
        exitTrigger: currentThesis.exit_trigger || (arch ? arch.defaultParams.exit_trigger : ''),
        turnoverTolerance:
          currentThesis.turnover_tolerance || (arch ? arch.defaultParams.turnover_tolerance : ''),
      });
      setIsDirty(false);
    } else {
      setFormData(formDataFromThesis(null));
      setSoulStyle(null);
      setCustomSoulText('');
      setSoulParams({ ...EMPTY_SOUL_PARAMS });
      setIsDirty(false);
    }
  }, [currentThesis]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (formData.targetReturnLevel && formData.targetReturnLevel.trim()) {
      const num = parseFloat(formData.targetReturnLevel);
      if (isNaN(num) || num < 0) {
        newErrors.targetReturnLevel = t('portfolio.mustBeValidNonNegativeNumber', {
          defaultValue: 'Must be a valid non-negative number',
        });
      }
    }
    if (formData.targetCashFlow && formData.targetCashFlow.trim()) {
      const num = parseFloat(formData.targetCashFlow);
      if (isNaN(num) || num < 0) {
        newErrors.targetCashFlow = t('portfolio.mustBeValidNonNegativeNumber', {
          defaultValue: 'Must be a valid non-negative number',
        });
      }
    }
    // The ε override must sit in the engine's band (2–25 percent here) — the API rejects the
    // rest with a 422, and a bad ε would otherwise degrade the whole book inside
    // the engine rather than being reported as a bad field.
    if (formData.esTargetOverride && formData.esTargetOverride.trim()) {
      const num = parseFloat(formData.esTargetOverride);
      if (isNaN(num) || num < 2 || num > 25) {
        newErrors.esTargetOverride = t('portfolio.esTargetRange', {
          defaultValue: 'Must be between 2 and 25 (percent)',
        });
      }
    }
    if (formData.comment && formData.comment.length > 5000) {
      newErrors.comment = t('portfolio.commentMaxLength', {
        defaultValue: 'Comment must be 5000 characters or less',
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Resolve the style name: archetype displayName or "Custom" */
  const resolveStyleName = (): string | null => {
    if (soulStyle === CUSTOM_SOUL_KEY) return soulParams.styleDescription?.trim() || 'Custom';
    return soulStyle;
  };

  /**
   * Post a persona version when any knowledge question was answered.
   *
   * Level rule — "highest answered" (documented choice over the spec's mode
   * alternative): with at most 3 questions a mode is almost always a 3-way
   * tie, so we post the most sophisticated level among ANSWERED questions.
   * Unanswered questions contribute nothing (a skip never implies
   * "intermediate"); when every question is skipped we post nothing and
   * downstream consumers fall back to their intermediate default.
   *
   * Failure is swallowed on purpose: the persona capture is auxiliary and
   * must never fail (or retry-block) the thesis save.
   */
  const maybeSavePersona = async () => {
    const answered = KNOWLEDGE_QUESTIONS.map((qid) => knowledgeAnswers[qid]).filter(
      (level): level is KnowledgeLevel => !!level
    );
    if (answered.length === 0) return;

    const highest = answered.reduce((a, b) => (KNOWLEDGE_RANK[a] >= KNOWLEDGE_RANK[b] ? a : b));
    try {
      await createPersonaMutation.mutateAsync({
        knowledge_level: highest,
        source: 'onboarding',
      });
    } catch (error) {
      console.warn('Failed to save persona (non-blocking):', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const resolvedStyle = resolveStyleName();
    const code = resolvedStyle ? getArchetypeCode(resolvedStyle) : null;
    const arch = code ? getArchetype(code) : null;

    setIsSaving(true);
    try {
      const apiData: any = {
        target_risk_level: formData.targetRiskLevel,
        target_return_level: formData.targetReturnLevel
          ? parseFloat(formData.targetReturnLevel)
          : null,
        target_cash_flow: formData.targetCashFlow ? parseFloat(formData.targetCashFlow) : null,
        comment: formData.comment || null,
        style_name: resolvedStyle,
        style_description: arch ? arch.description : soulParams.styleDescription || null,
        target_positions: soulParams.targetPositions || null,
        max_single_weight: soulParams.maxSingleWeight || null,
        rebalance_direction: soulParams.rebalanceDirection || null,
        exit_trigger: soulParams.exitTrigger || null,
        turnover_tolerance: soulParams.turnoverTolerance || null,
        // Sent explicitly (null when blank) so clearing a declared override is
        // possible: the update route treats an explicit null as "clear".
        es_target_override:
          formData.esTargetOverride && formData.esTargetOverride.trim()
            ? parseFloat(formData.esTargetOverride) / 100
            : null,
      };

      if (theses.length === 0 || !currentThesis) {
        await createMutation.mutateAsync(apiData);
      } else {
        await updateMutation.mutateAsync({ thesisId: currentThesis.id, data: apiData });
      }

      // Only after the thesis save succeeded; its own errors are swallowed.
      await maybeSavePersona();

      showSuccess(
        t('portfolio.thesisSavedSuccessfully', { defaultValue: 'Thesis saved successfully' })
      );
      setIsDirty(false);
    } catch (error: any) {
      showError(
        error.message ||
          t('portfolio.failedToSaveThesis', { defaultValue: 'Failed to save thesis' })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentThesis) return;
    const confirmed = await showConfirm(
      t('portfolio.deleteThisVersion', { defaultValue: 'Delete This Version' }),
      t('portfolio.deleteThesisConfirmation', {
        defaultValue:
          'Are you sure you want to delete the thesis from {{date}}? This action cannot be undone.',
        date: formatVersionDate(currentThesis.updated_at),
      }),
      {
        confirmText: t('common.delete', { defaultValue: 'Delete' }),
        cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
        destructiveAction: true,
      }
    );
    if (confirmed) {
      setIsSaving(true);
      try {
        await deleteMutation.mutateAsync(currentThesis.id);
        showSuccess(
          t('portfolio.versionDeletedSuccessfully', {
            defaultValue: 'Version deleted successfully',
          })
        );
      } catch (error: any) {
        showError(
          error.message ||
            t('portfolio.failedToDeleteThesis', { defaultValue: 'Failed to delete thesis' })
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    if (currentThesis) {
      setFormData(formDataFromThesis(currentThesis));
      const style = currentThesis.style_name || null;
      if (style && !isArchetypeName(style)) {
        setSoulStyle(CUSTOM_SOUL_KEY);
        setCustomSoulText(style);
      } else if (style) {
        setSoulStyle(style);
      } else {
        setSoulStyle(null);
        setCustomSoulText('');
      }
      setSoulParams({
        styleName: style || '',
        styleDescription: currentThesis.style_description || '',
        targetPositions: currentThesis.target_positions || '',
        maxSingleWeight: currentThesis.max_single_weight || '',
        rebalanceDirection: currentThesis.rebalance_direction || '',
        exitTrigger: currentThesis.exit_trigger || '',
        turnoverTolerance: currentThesis.turnover_tolerance || '',
      });
      setIsDirty(false);
    }
  };

  const handleVersionChange = (index: number) => {
    setSelectedVersionIndex(index);
    const version = theses[index];
    setFormData(formDataFromThesis(version));
    const style = version.style_name || null;
    if (style && !isArchetypeName(style)) {
      setSoulStyle(CUSTOM_SOUL_KEY);
      setCustomSoulText(style);
    } else if (style) {
      setSoulStyle(style);
    } else {
      setSoulStyle(null);
      setCustomSoulText('');
    }
    // Build soulParams — fall back to archetype defaults if params are empty
    const code = style ? getArchetypeCode(style) : null;
    const arch = code ? getArchetype(code) : null;
    setSoulParams({
      styleName: style || '',
      styleDescription: version.style_description || (arch ? arch.description : ''),
      targetPositions:
        version.target_positions || (arch ? arch.defaultParams.target_positions : ''),
      maxSingleWeight:
        version.max_single_weight || (arch ? arch.defaultParams.max_single_weight : ''),
      rebalanceDirection:
        version.rebalance_direction || (arch ? arch.defaultParams.rebalance_direction : ''),
      exitTrigger: version.exit_trigger || (arch ? arch.defaultParams.exit_trigger : ''),
      turnoverTolerance:
        version.turnover_tolerance || (arch ? arch.defaultParams.turnover_tolerance : ''),
    });
    setIsDirty(false);
  };

  const hasContent = (): boolean => {
    return (
      formData.targetRiskLevel !== 'Moderate' ||
      (formData.targetReturnLevel && formData.targetReturnLevel.trim() !== '') ||
      (formData.targetCashFlow && formData.targetCashFlow.trim() !== '') ||
      (formData.esTargetOverride && formData.esTargetOverride.trim() !== '') ||
      (formData.comment && formData.comment.trim() !== '') ||
      !!resolveStyleName()
    );
  };

  const hasUnsavedChanges = (): boolean => {
    if (!currentThesis) return false;
    // Compare against the SAME conversion used to populate the form, so a
    // fraction→percent field can never look permanently dirty.
    const saved = formDataFromThesis(currentThesis);
    return (
      formData.targetRiskLevel !== saved.targetRiskLevel ||
      formData.targetReturnLevel !== saved.targetReturnLevel ||
      formData.targetCashFlow !== saved.targetCashFlow ||
      formData.comment !== saved.comment ||
      formData.esTargetOverride !== saved.esTargetOverride ||
      resolveStyleName() !== (currentThesis.style_name || '') ||
      soulParams.targetPositions !== (currentThesis.target_positions || '') ||
      soulParams.maxSingleWeight !== (currentThesis.max_single_weight || '') ||
      soulParams.rebalanceDirection !== (currentThesis.rebalance_direction || '') ||
      soulParams.exitTrigger !== (currentThesis.exit_trigger || '') ||
      soulParams.turnoverTolerance !== (currentThesis.turnover_tolerance || '')
    );
  };

  // Is the currently selected style an archetype? (read-only params)
  const isArchetypeSelected = soulStyle !== null && soulStyle !== CUSTOM_SOUL_KEY;

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
          {t('portfolio.loadingThesis', { defaultValue: 'Loading thesis...' })}
        </Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView style={styles.outerContainer} contentContainerStyle={{ paddingBottom: 300 }}>
        <Card style={styles.card}>
          {/* Title Row with Version Selector */}
          <View style={styles.titleRow}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {t('portfolio.portfolioThesis', { defaultValue: 'Portfolio Thesis' })}
            </Text>
            {theses.length > 1 && (
              <View
                style={[
                  styles.versionPickerWrapper,
                  {
                    borderColor: theme.colors.border,
                    borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                    backgroundColor: theme.colors.backgroundElevated,
                  },
                ]}
              >
                <Picker
                  selectedValue={selectedVersionIndex}
                  onValueChange={(itemValue: number) => handleVersionChange(itemValue)}
                  enabled={!isSaving}
                  style={[styles.versionPicker, { color: theme.colors.text }]}
                  dropdownIconColor={theme.colors.textSecondary}
                  mode="dropdown"
                >
                  {theses.map((thesis, index) => (
                    <Picker.Item
                      key={thesis.id}
                      label={
                        index === 0
                          ? t('portfolio.latest', { defaultValue: 'Latest' })
                          : formatVersionDate(thesis.updated_at)
                      }
                      value={index}
                      color={theme.colors.text}
                    />
                  ))}
                </Picker>
                {Platform.OS === 'web' && (
                  <style>{`select option { background-color: ${theme.colors.backgroundElevated}; color: ${theme.colors.text}; }`}</style>
                )}
              </View>
            )}
          </View>

          {/* Version Info */}
          {selectedVersionIndex > 0 && currentThesis && (
            <View
              style={[styles.versionBanner, { backgroundColor: theme.colors.warningBackground }]}
            >
              <Text style={[styles.versionBannerText, { color: theme.colors.warningText }]}>
                {t('portfolio.viewingHistoricalVersion', {
                  defaultValue: 'Viewing historical version from {{date}}',
                  date: formatVersionDate(currentThesis.updated_at),
                })}
              </Text>
              <Text style={[styles.versionBannerSubtext, { color: theme.colors.warningText }]}>
                {t('portfolio.savingWillCreateNewVersion', {
                  defaultValue: 'Saving will create a new version',
                })}
              </Text>
            </View>
          )}

          {/* Target Risk Level */}
          <View style={styles.fieldGroup}>
            <Select
              label={t('portfolio.targetRiskLevel', { defaultValue: 'Target Risk Level' })}
              options={RISK_LEVEL_OPTIONS}
              value={formData.targetRiskLevel}
              onChange={(value) => {
                setFormData({ ...formData, targetRiskLevel: value as any });
                setIsDirty(true);
              }}
              disabled={isSaving}
              placeholder={t('portfolio.selectRiskTolerance', {
                defaultValue: 'Select risk tolerance',
              })}
            />
          </View>

          {/* Target Return Level & Cash Flow */}
          <View style={styles.rowFieldGroup}>
            <View style={styles.halfField}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.targetReturn', { defaultValue: 'Target Return (%)' })}
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: errors.targetReturnLevel
                      ? theme.colors.error
                      : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder={t('portfolio.annualReturnPercent', {
                  defaultValue: 'Annual return %%',
                })}
                placeholderTextColor={theme.colors.textTertiary}
                value={formData.targetReturnLevel}
                onChangeText={(text) => {
                  setFormData({ ...formData, targetReturnLevel: text });
                  setErrors({ ...errors, targetReturnLevel: undefined });
                  setIsDirty(true);
                }}
                keyboardType="decimal-pad"
                editable={!isSaving}
              />
              {errors.targetReturnLevel && (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.targetReturnLevel}
                </Text>
              )}
            </View>
            <View style={styles.halfField}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.cashFlow', {
                  defaultValue: 'Cash Flow ({{currency}})',
                  currency: baseCurrency,
                })}
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: errors.targetCashFlow ? theme.colors.error : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder={t('portfolio.annualCashFlow', { defaultValue: 'Annual cash flow' })}
                placeholderTextColor={theme.colors.textTertiary}
                value={formData.targetCashFlow}
                onChangeText={(text) => {
                  setFormData({ ...formData, targetCashFlow: text });
                  setErrors({ ...errors, targetCashFlow: undefined });
                  setIsDirty(true);
                }}
                keyboardType="decimal-pad"
                editable={!isSaving}
              />
              {errors.targetCashFlow && (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.targetCashFlow}
                </Text>
              )}
            </View>
          </View>

          {/* Risk budget. The denominator comes from position values; cash is
              represented as a CASH position. The override replaces the profile default. */}
          <View style={styles.rowFieldGroup}>
            <View style={styles.halfField}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                {t('portfolio.esTargetOverride', { defaultValue: 'Risk Budget (%)' })}
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: errors.esTargetOverride ? theme.colors.error : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder={t('portfolio.esTargetPlaceholder', {
                  defaultValue: '{{value}}% from {{profile}}',
                  value: esTargetProfilePercent,
                  profile: esTargetProfileLabel,
                })}
                placeholderTextColor={theme.colors.textTertiary}
                value={formData.esTargetOverride}
                onChangeText={(text) => {
                  setFormData({ ...formData, esTargetOverride: text });
                  setErrors({ ...errors, esTargetOverride: undefined });
                  setIsDirty(true);
                }}
                keyboardType="decimal-pad"
                editable={!isSaving}
              />
              {errors.esTargetOverride ? (
                <Text style={[styles.errorText, { color: theme.colors.error }]}>
                  {errors.esTargetOverride}
                </Text>
              ) : (
                <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                  {t('portfolio.esTargetHelp', {
                    defaultValue: 'Profile default: {{value}}%. Enter 2–25 to override.',
                    value: esTargetProfilePercent,
                  })}
                </Text>
              )}
              <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                {t('portfolio.riskDenominatorHelp', {
                  defaultValue:
                    'Denominator: the sum of your position values. Hold cash as a CASH position.',
                })}
              </Text>
              <TouchableOpacity
                style={styles.addCashButton}
                disabled={isSaving}
                activeOpacity={0.7}
                onPress={() => navigateToPortfolio('AddCash')}
              >
                <Text style={styles.addCashButtonText}>
                  {t('portfolio.addCashPosition', { defaultValue: 'Add cash position' })}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Investment Style (SOUL) — collapsible combo box */}
          <View style={styles.fieldGroup}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.investmentStyle', { defaultValue: 'Investment Style' })}
            </Text>

            {/* Trigger */}
            <TouchableOpacity
              activeOpacity={0.7}
              disabled={isSaving}
              onPress={() => setSoulPickerOpen(!soulPickerOpen)}
              style={[
                styles.soulTrigger,
                {
                  borderColor: soulPickerOpen ? theme.colors.primary : theme.colors.border,
                  backgroundColor: theme.colors.backgroundElevated,
                },
              ]}
            >
              <View style={styles.soulTriggerContent}>
                {soulStyle && soulStyle !== CUSTOM_SOUL_KEY ? (
                  <>
                    <Text style={[styles.soulTriggerName, { color: theme.colors.text }]}>
                      {(() => {
                        const c = getArchetypeCode(soulStyle);
                        return c ? t(`souls.${c}.title`) : soulStyle;
                      })()}
                    </Text>
                    <Text style={[styles.soulTriggerQuote, { color: theme.colors.textSecondary }]}>
                      {(() => {
                        const c = getArchetypeCode(soulStyle);
                        return c ? t(`souls.${c}.quote`) : '';
                      })()}
                    </Text>
                  </>
                ) : soulStyle === CUSTOM_SOUL_KEY ? (
                  <Text style={[styles.soulTriggerName, { color: theme.colors.text }]}>
                    {customSoulText ||
                      t('settings.soul.custom', {
                        defaultValue: 'Custom — describe your own style',
                      })}
                  </Text>
                ) : (
                  <Text style={[styles.soulTriggerName, { color: theme.colors.textTertiary }]}>
                    {t('portfolio.selectInvestmentStyle', {
                      defaultValue: 'Select your investment style',
                    })}
                  </Text>
                )}
              </View>
              <Text style={[styles.soulChevron, { color: theme.colors.textSecondary }]}>
                {soulPickerOpen ? '▲' : '▼'}
              </Text>
            </TouchableOpacity>

            {/* Expanded options */}
            {soulPickerOpen && (
              <View style={[styles.soulDropdown, { borderColor: theme.colors.border }]}>
                <ScrollView style={styles.soulDropdownScroll} nestedScrollEnabled>
                  {ARCHETYPES.map((a) => {
                    const isSelected = soulStyle === a.displayName;
                    return (
                      <TouchableOpacity
                        key={a.code}
                        activeOpacity={0.7}
                        disabled={isSaving}
                        onPress={() => {
                          setSoulStyle(a.displayName);
                          setCustomSoulText('');
                          setSoulParams({
                            styleName: a.displayName,
                            styleDescription: a.description,
                            targetPositions: a.defaultParams.target_positions,
                            maxSingleWeight: a.defaultParams.max_single_weight,
                            rebalanceDirection: a.defaultParams.rebalance_direction,
                            exitTrigger: a.defaultParams.exit_trigger,
                            turnoverTolerance: a.defaultParams.turnover_tolerance,
                          });
                          setSoulPickerOpen(false);
                          setIsDirty(true);
                        }}
                        style={[
                          styles.soulOption,
                          {
                            backgroundColor: isSelected
                              ? theme.colors.selectedBackground
                              : theme.colors.surface,
                            borderBottomColor: theme.colors.divider,
                          },
                          isSelected && {
                            borderLeftWidth: 3,
                            borderLeftColor: theme.colors.primary,
                          },
                        ]}
                      >
                        <Text style={[styles.soulOptionName, { color: theme.colors.text }]}>
                          {t(`souls.${a.code}.title`)}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                  {/* Custom option */}
                  <TouchableOpacity
                    activeOpacity={0.7}
                    disabled={isSaving}
                    onPress={() => {
                      setSoulStyle(CUSTOM_SOUL_KEY);
                      setCustomSoulText('');
                      setSoulParams({
                        styleName: '',
                        styleDescription: '',
                        targetPositions: '',
                        maxSingleWeight: '',
                        rebalanceDirection: '',
                        exitTrigger: '',
                        turnoverTolerance: '',
                      });
                      setSoulPickerOpen(false);
                      setIsDirty(true);
                    }}
                    style={[
                      styles.soulOption,
                      {
                        backgroundColor:
                          soulStyle === CUSTOM_SOUL_KEY
                            ? theme.colors.selectedBackground
                            : theme.colors.surface,
                      },
                      soulStyle === CUSTOM_SOUL_KEY && {
                        borderLeftWidth: 3,
                        borderLeftColor: theme.colors.primary,
                      },
                    ]}
                  >
                    <Text style={[styles.soulOptionName, { color: theme.colors.text }]}>
                      {t('settings.soul.custom', {
                        defaultValue: 'Custom — describe your own style',
                      })}
                    </Text>
                  </TouchableOpacity>
                </ScrollView>
              </View>
            )}

            {/* Style description (read-only for archetype) */}
            {isArchetypeSelected && soulParams.styleDescription ? (
              <View
                style={[
                  styles.paramCard,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                    marginTop: theme.spacing.sm,
                  },
                ]}
              >
                <Text style={[styles.paramDesc, { color: theme.colors.textSecondary }]}>
                  {soulParams.styleDescription}
                </Text>
              </View>
            ) : (
              soulStyle === CUSTOM_SOUL_KEY &&
              !soulPickerOpen && (
                <TextInput
                  style={[
                    styles.input,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                      marginTop: theme.spacing.sm,
                    },
                  ]}
                  placeholder={t('portfolio.styleDescription', {
                    defaultValue: 'Describe your investment methodology...',
                  })}
                  placeholderTextColor={theme.colors.textTertiary}
                  value={soulParams.styleDescription}
                  onChangeText={(text) => {
                    setSoulParams((prev) => ({ ...prev, styleDescription: text }));
                    setIsDirty(true);
                  }}
                  editable={!isSaving}
                />
              )
            )}

            {/* 5 Style Parameters */}
            {soulStyle && !soulPickerOpen && (
              <View style={{ marginTop: theme.spacing.sm }}>
                {/* Target Positions — free text */}
                <ParamField
                  label={t('portfolio.soulParams.targetPositions', {
                    defaultValue: 'Target positions',
                  })}
                  value={soulParams.targetPositions}
                  editable={!isArchetypeSelected}
                  onChange={(v) => {
                    setSoulParams((prev) => ({ ...prev, targetPositions: v }));
                    setIsDirty(true);
                  }}
                  theme={theme}
                  styles={styles}
                />
                {/* Max Single Weight — free text */}
                <ParamField
                  label={t('portfolio.soulParams.maxSingleWeight', {
                    defaultValue: 'Max single weight',
                  })}
                  value={soulParams.maxSingleWeight}
                  editable={!isArchetypeSelected}
                  onChange={(v) => {
                    setSoulParams((prev) => ({ ...prev, maxSingleWeight: v }));
                    setIsDirty(true);
                  }}
                  theme={theme}
                  styles={styles}
                />
                {/* Rebalance Direction — dropdown */}
                <ParamField
                  label={t('portfolio.soulParams.rebalanceDirection', {
                    defaultValue: 'Rebalance direction',
                  })}
                  value={soulParams.rebalanceDirection}
                  editable={!isArchetypeSelected}
                  options={REBALANCE_DIRECTIONS}
                  onChange={(v) => {
                    setSoulParams((prev) => ({ ...prev, rebalanceDirection: v }));
                    setIsDirty(true);
                  }}
                  theme={theme}
                  styles={styles}
                />
                {/* Exit Trigger — dropdown */}
                <ParamField
                  label={t('portfolio.soulParams.exitTrigger', { defaultValue: 'Exit trigger' })}
                  value={soulParams.exitTrigger}
                  editable={!isArchetypeSelected}
                  options={EXIT_TRIGGERS}
                  onChange={(v) => {
                    setSoulParams((prev) => ({ ...prev, exitTrigger: v }));
                    setIsDirty(true);
                  }}
                  theme={theme}
                  styles={styles}
                />
                {/* Turnover Tolerance — dropdown */}
                <ParamField
                  label={t('portfolio.soulParams.turnoverTolerance', {
                    defaultValue: 'Turnover tolerance',
                  })}
                  value={soulParams.turnoverTolerance}
                  editable={!isArchetypeSelected}
                  options={TURNOVER_TOLERANCES}
                  onChange={(v) => {
                    setSoulParams((prev) => ({ ...prev, turnoverTolerance: v }));
                    setIsDirty(true);
                  }}
                  theme={theme}
                  styles={styles}
                />
              </View>
            )}
          </View>

          {/* Knowledge questions (investor persona) — every question is
              skippable; unanswered questions post nothing. */}
          <View style={styles.fieldGroup}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.knowledge.title', {
                defaultValue: 'About your investing experience',
              })}
            </Text>
            <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
              {t('portfolio.knowledge.help', {
                defaultValue:
                  'Answer any or all — it helps us pitch explanations at the right level. Skip freely.',
              })}
            </Text>
            {KNOWLEDGE_QUESTIONS.map((qid) => (
              <View key={qid} style={styles.knowledgeQuestion}>
                <Text style={[styles.knowledgeQuestionText, { color: theme.colors.text }]}>
                  {t(`portfolio.knowledge.${qid}.text`)}
                </Text>
                {KNOWLEDGE_LEVELS.map((level) => {
                  const selected = knowledgeAnswers[qid] === level;
                  return (
                    <TouchableOpacity
                      key={level}
                      testID={`knowledge-option-${qid}-${level}`}
                      activeOpacity={0.7}
                      disabled={isSaving}
                      onPress={() => {
                        setKnowledgeAnswers((prev) => ({ ...prev, [qid]: level }));
                      }}
                      style={[
                        styles.knowledgeOption,
                        {
                          backgroundColor: selected
                            ? theme.colors.selectedBackground
                            : theme.colors.backgroundElevated,
                          borderColor: theme.colors.border,
                        },
                        selected && {
                          borderLeftWidth: 3,
                          borderLeftColor: theme.colors.primary,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.knowledgeOptionText,
                          { color: selected ? theme.colors.text : theme.colors.textSecondary },
                        ]}
                      >
                        {t(`portfolio.knowledge.${qid}.${level}`)}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
                <TouchableOpacity
                  testID={`knowledge-skip-${qid}`}
                  style={styles.knowledgeSkip}
                  disabled={isSaving}
                  activeOpacity={0.7}
                  onPress={() => {
                    setKnowledgeAnswers((prev) => {
                      const next = { ...prev };
                      delete next[qid];
                      return next;
                    });
                  }}
                >
                  <Text style={[styles.knowledgeSkipText, { color: theme.colors.textTertiary }]}>
                    {t('portfolio.knowledge.skip', { defaultValue: 'Skip' })}
                  </Text>
                </TouchableOpacity>
              </View>
            ))}
          </View>

          {/* Last Updated */}
          {currentThesis && currentThesis.updated_at && (
            <Text style={[styles.lastUpdated, { color: theme.colors.textTertiary }]}>
              {formatLastUpdated(currentThesis.updated_at)}
            </Text>
          )}

          {/* Actions */}
          <View style={styles.actions}>
            {currentThesis ? (
              <>
                {!hasUnsavedChanges() && (
                  <TouchableOpacity
                    style={[
                      styles.deleteButton,
                      { borderColor: theme.colors.error, minHeight: 48 },
                    ]}
                    onPress={handleDelete}
                    disabled={isSaving}
                  >
                    <Text style={[styles.deleteButtonText, { color: theme.colors.error }]}>
                      {t('common.delete', { defaultValue: 'Delete' })}
                    </Text>
                  </TouchableOpacity>
                )}
                {hasUnsavedChanges() && (
                  <TouchableOpacity
                    style={[
                      styles.cancelButton,
                      { borderColor: theme.colors.border, minHeight: 48 },
                    ]}
                    onPress={handleCancel}
                    disabled={isSaving}
                  >
                    <Text style={[styles.cancelButtonText, { color: theme.colors.textSecondary }]}>
                      {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Text>
                  </TouchableOpacity>
                )}
                <Button
                  title={t('common.save', { defaultValue: 'Save' })}
                  onPress={handleSave}
                  disabled={isSaving || (!isDirty && !hasContent())}
                  loading={isSaving}
                />
              </>
            ) : (
              <Button
                title={t('common.save', { defaultValue: 'Save' })}
                onPress={handleSave}
                disabled={!hasContent() || isSaving}
                loading={isSaving}
              />
            )}
          </View>

          {/* Changelog */}
          {theses.length > 0 && (
            <PortfolioThesisChangelog versions={theses} currency={baseCurrency} />
          )}
        </Card>
      </ScrollView>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Param Field — read-only text or editable text/dropdown            */
/* ------------------------------------------------------------------ */

interface ParamFieldProps {
  label: string;
  value: string;
  editable: boolean;
  options?: string[];
  onChange: (v: string) => void;
  theme: any;
  styles: any;
}

function ParamField({ label, value, editable, options, onChange, theme, styles }: ParamFieldProps) {
  if (!editable) {
    // Read-only display for archetype
    return (
      <View style={[styles.paramRow, { borderBottomColor: theme.colors.divider }]}>
        <Text style={[styles.paramLabel, { color: theme.colors.textTertiary }]}>{label}</Text>
        <Text style={[styles.paramValue, { color: theme.colors.text }]}>{value || '—'}</Text>
      </View>
    );
  }

  if (options && options.length > 0) {
    // Dropdown for custom
    return (
      <View style={{ marginBottom: 4 }}>
        <Text style={[styles.paramLabel, { color: theme.colors.textTertiary }]}>{label}</Text>
        <View
          style={[
            styles.pickerWrapper,
            { borderColor: theme.colors.border, backgroundColor: theme.colors.backgroundElevated },
          ]}
        >
          <Picker
            selectedValue={value}
            onValueChange={onChange}
            style={{ color: theme.colors.text, fontSize: theme.typography.fontSizeSm }}
            dropdownIconColor={theme.colors.textSecondary}
            mode="dropdown"
          >
            <Picker.Item label="—" value="" color={theme.colors.textTertiary} />
            {options.map((opt) => (
              <Picker.Item key={opt} label={opt} value={opt} color={theme.colors.text} />
            ))}
          </Picker>
        </View>
      </View>
    );
  }

  // Free-text input for custom
  return (
    <View style={{ marginBottom: 4 }}>
      <Text style={[styles.paramLabel, { color: theme.colors.textTertiary }]}>{label}</Text>
      <TextInput
        style={[
          styles.paramInput,
          {
            backgroundColor: theme.colors.backgroundElevated,
            borderColor: theme.colors.border,
            color: theme.colors.text,
          },
        ]}
        value={value}
        onChangeText={onChange}
        placeholderTextColor={theme.colors.textTertiary}
        editable
      />
    </View>
  );
}

/* ------------------------------------------------------------------ */
/*  Themed styles                                                      */
/* ------------------------------------------------------------------ */

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    outerContainer: { flex: 1 },
    card: { margin: 16, padding: 16 },
    titleRow: { marginBottom: theme.spacing.lg },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    versionPickerWrapper: {
      borderRadius: theme.radii.md,
      marginTop: theme.spacing.sm,
      overflow: 'hidden',
    },
    versionPicker: {
      height: Platform.OS === 'web' ? 44 : undefined,
      fontSize: theme.typography.fontSizeMd,
      backgroundColor: 'transparent',
    },
    versionBanner: {
      padding: theme.spacing.md,
      borderRadius: theme.radii.md,
      marginBottom: theme.spacing.lg,
      borderLeftWidth: 4,
      borderLeftColor: theme.colors.warningBorder,
    },
    versionBannerText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 2,
    },
    versionBannerSubtext: { fontSize: theme.typography.fontSizeXs, opacity: 0.8 },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    fieldGroup: { marginBottom: theme.spacing.lg },
    rowFieldGroup: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.lg,
    },
    halfField: { flex: 1 },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
    },
    textArea: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 100,
      textAlignVertical: 'top',
      color: theme.colors.text,
    },
    errorText: { fontSize: theme.typography.fontSizeXs, color: theme.colors.error, marginTop: 2 },
    helperText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
      marginTop: theme.spacing.xxs,
    },
    addCashButton: {
      alignSelf: 'flex-start',
      marginTop: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
    },
    addCashButtonText: {
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    lastUpdated: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
      fontStyle: 'italic',
      marginTop: theme.spacing.sm,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 32,
    },
    loadingText: {
      marginTop: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
    actions: { flexDirection: 'row' as 'row', gap: theme.spacing.md, marginTop: theme.spacing.lg },
    deleteButton: {
      flex: 1,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: 14,
      paddingHorizontal: 20,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      minWidth: 120,
    },
    deleteButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    cancelButton: {
      flex: 1,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: 14,
      paddingHorizontal: 20,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      minWidth: 120,
    },
    cancelButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    // SOUL collapsible combo box
    soulTrigger: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm + 2,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
    },
    soulTriggerContent: { flex: 1 },
    soulTriggerName: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold as any,
    },
    soulTriggerQuote: {
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic' as const,
      marginTop: 2,
      flexShrink: 1,
    },
    soulTriggerDesc: { fontSize: theme.typography.fontSizeSm, marginTop: 2, flexShrink: 1 },
    soulChevron: { fontSize: theme.typography.fontSizeSm, marginLeft: theme.spacing.sm },
    soulDropdown: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderTopWidth: 0,
      borderBottomLeftRadius: theme.radii.md,
      borderBottomRightRadius: theme.radii.md,
      overflow: 'hidden' as const,
    },
    soulDropdownScroll: { maxHeight: 320 },
    soulOption: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm + 2,
      borderBottomWidth: 1,
    },
    soulOptionName: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold as any,
    },
    soulOptionQuote: {
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic' as const,
      marginTop: 2,
      flexShrink: 1,
    },
    soulOptionDesc: { fontSize: theme.typography.fontSizeSm, marginTop: 2, flexShrink: 1 },
    // Param display styles
    paramCard: {
      borderWidth: 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
    },
    paramDesc: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 18,
    },
    paramRow: {
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      alignItems: 'center' as const,
      paddingVertical: theme.spacing.xs + 2,
      borderBottomWidth: 1,
    },
    paramLabel: {
      fontSize: theme.typography.fontSizeXs,
      flex: 1,
    },
    paramValue: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: '500' as any,
      flex: 1,
      textAlign: 'right' as const,
    },
    paramInput: {
      borderWidth: 1,
      borderRadius: theme.radii.sm,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      fontSize: theme.typography.fontSizeSm,
    },
    pickerWrapper: {
      borderWidth: 1,
      borderRadius: theme.radii.sm,
      overflow: 'hidden',
    },
    // Knowledge questions (persona)
    knowledgeQuestion: { marginTop: theme.spacing.md },
    knowledgeQuestionText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      lineHeight: 20,
      marginBottom: theme.spacing.xs,
    },
    knowledgeOption: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderLeftWidth: 3,
      borderLeftColor: 'transparent',
      borderRadius: theme.radii.sm,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      marginBottom: theme.spacing.xs,
    },
    knowledgeOptionText: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 18,
    },
    knowledgeSkip: {
      alignSelf: 'flex-end',
      paddingVertical: theme.spacing.xxs,
      paddingHorizontal: theme.spacing.sm,
    },
    knowledgeSkipText: {
      fontSize: theme.typography.fontSizeXs,
      textDecorationLine: 'underline',
    },
  });
}
